import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { HR } from 'foundations-components/transitional/components/HR';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Select } from 'foundations-components/transitional/components/Select';
import { Tile } from 'foundations-components/transitional/components/Tile';
import { Link } from 'foundations-components/transitional/components/Link';
import { ReviewWizardRatingInput, ReviewWizardTextInput } from 'trellis-ecosystem';
import { usePrevious } from 'marketplace-ui-common/hooks/usePrevious';
import { EcosystemReviewWizard } from '../../../components/wizard/EcosystemReviewWizard';
import { POSITIVE_RATING_MINIMUM } from '../../../constants/ReviewLimits';
import { PENDING } from '../../../constants/UnreviewableReason';
import { useGeneratedReviewTitle } from '../../../hooks/useGeneratedReviewTitle';
import { AppReviewWizardHeader } from './AppReviewWizardHeader';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DETAILED_RATING_AXES = ['VALUE', 'USABILITY', 'FUNCTIONALITY', 'SUPPORT_DOCS'];
const EDIT_REASONS = ['MISSPELLING', 'NEW_INFORMATION', 'ISSUE_RESOLVED', 'APPROACHED_BY_PARTNER', 'OTHER'];
function getCompletedAnswerFields(review) {
  return Object.entries(review.content).filter(([field, value]) => field && !!value).map(([field]) => field);
}
export function AppReviewWizard({
  appIconUrl,
  appName,
  editedReview,
  initialRating,
  isPublic,
  offeringId,
  onClose,
  onSuccess,
  onTrackInteraction,
  supportLink,
  unreviewableReason
}) {
  const isEditing = !!editedReview;
  const [editReason, setEditReason] = useState('');
  let defaultReview;
  if (isEditing && editedReview) {
    const {
      answers,
      ratings,
      tags
    } = editedReview.toJSON();
    defaultReview = {
      content: answers,
      ratings,
      tags
    };
  } else {
    const emptyReview = {
      content: {},
      ratings: {
        OVERALL: initialRating
      },
      tags: {}
    };
    try {
      const storedReview = JSON.parse(sessionStorage.getItem(`APP_${offeringId}_review`));
      defaultReview = storedReview || emptyReview;
    } catch (__error) {
      defaultReview = emptyReview;
    }
  }
  const [originalReview] = useState(isEditing ? {
    content: editedReview.toJSON().answers,
    ratings: editedReview.toJSON().ratings,
    tags: editedReview.toJSON().tags
  } : undefined);
  const [overallRating, setOverallRating] = useState(defaultReview.ratings['OVERALL'] || undefined);
  const [reviewAnswers, setReviewAnswers] = useState(defaultReview.content || {});
  const [reviewRatings, setReviewRatings] = useState(defaultReview.ratings || {});
  const [customTitleProvided, setCustomTitleProvided] = useState(!!defaultReview.content.TITLE);
  const [injectedTitle, setInjectedTitle] = useState();
  const [completedAnswerFields, setCompletedAnswerFields] = useState(getCompletedAnswerFields(defaultReview));
  const [hasDetailedRatings, setHasDetailedRatings] = useState(Object.keys(defaultReview.ratings).length > 1);
  const [showDetailedRatings, setShowDetailedRatings] = useState(hasDetailedRatings);
  const [requireFields, setRequireFields] = useState(overallRating !== undefined && overallRating < POSITIVE_RATING_MINIMUM || !!defaultReview.content.TITLE || !!defaultReview.content.REVIEW || !!defaultReview.content.PROS || !!defaultReview.content.CONS);
  const validationSchema = {
    content: {
      TITLE: {
        required: requireFields
      },
      REVIEW: {
        required: requireFields
      }
    },
    ratings: {
      OVERALL: {
        required: true
      }
    }
  };
  const {
    generativeAiEnabled,
    generatedTitle,
    loading
  } = useGeneratedReviewTitle({
    answers: reviewAnswers,
    ratings: reviewRatings,
    entityName: appName,
    reviewType: 'APP'
  });
  const prevCustomTitleProvided = usePrevious(customTitleProvided);
  // Automatically inject the generated title if the user hasn't provided a custom title
  useEffect(() => {
    if (!customTitleProvided) {
      setInjectedTitle(generatedTitle);
    }
    if (prevCustomTitleProvided !== undefined && prevCustomTitleProvided !== customTitleProvided) {
      const action = customTitleProvided ? 'generated review title edited' : 'generated review title selected';
      onTrackInteraction({
        action,
        section: 'review'
      });
    }
  }, [customTitleProvided, generatedTitle, onTrackInteraction, prevCustomTitleProvided]);
  useEffect(() => {
    onTrackInteraction({
      action: isEditing ? 'open edit review wizard' : 'open review wizard',
      section: 'review'
    });
  }, [isEditing, onTrackInteraction]);
  useEffect(() => {
    if (!isEditing && unreviewableReason && unreviewableReason !== `ReviewError.${PENDING}`) {
      FloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `marketplace-ui-reviews.error.${unreviewableReason}`
        }),
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-reviews.error.ReviewError.GENERIC_UNREVIEWABLE"
        }),
        type: 'danger'
      });
    }
  }, [isEditing, unreviewableReason]);
  const handleSuccess = useCallback(() => {
    onTrackInteraction({
      action: 'submit review',
      section: 'review'
    });
    sessionStorage.removeItem(`APP_${offeringId}_review`);
    onSuccess();
  }, [onSuccess, offeringId, onTrackInteraction]);
  const handleClose = useCallback(() => {
    onTrackInteraction({
      action: 'dismiss review wizard',
      section: 'review'
    });
    onClose();
  }, [onClose, onTrackInteraction]);
  const onChange = useCallback(review => {
    setReviewAnswers(structuredClone(review.content));
    setReviewRatings(structuredClone(review.ratings));
    if (review.content.TITLE || review.content.REVIEW || review.content.PROS || review.content.CONS) {
      setRequireFields(true);
    } else {
      setRequireFields(review.ratings['OVERALL'] < POSITIVE_RATING_MINIMUM);
    }
    if (Object.keys(review.ratings).length > 1 && !hasDetailedRatings) {
      onTrackInteraction({
        action: 'add detailed rating',
        section: 'review'
      });
      setHasDetailedRatings(true);
    }
    if (review.ratings['OVERALL'] !== overallRating) {
      onTrackInteraction({
        action: 'set overall rating',
        section: 'review',
        value: review.ratings['OVERALL']
      });
      setOverallRating(review.ratings['OVERALL']);
    }
    const answerFields = getCompletedAnswerFields(review);
    answerFields.forEach(field => {
      if (!completedAnswerFields.includes(field)) {
        onTrackInteraction({
          action: 'add review answer',
          section: 'review',
          value: field
        });
      }
    });
    setCompletedAnswerFields(answerFields);
    if (!isEditing) {
      sessionStorage.setItem(`APP_${offeringId}_review`, JSON.stringify(review));
    }
  }, [completedAnswerFields, hasDetailedRatings, isEditing, offeringId, onTrackInteraction, overallRating]);
  const reviewFormatter = useCallback(review => {
    const filteredReview = structuredClone(review);
    if (!showDetailedRatings) {
      DETAILED_RATING_AXES.forEach(axis => delete filteredReview.ratings[axis]);
    }
    return filteredReview;
  }, [showDetailedRatings]);
  const WizardHeader = useMemo(() => () => /*#__PURE__*/_jsx(AppReviewWizardHeader, {
    appIconUrl: appIconUrl,
    appName: appName,
    isEditing: isEditing
  }), [appIconUrl, appName, isEditing]);
  if (isPublic || !isEditing && unreviewableReason) {
    return null;
  }
  return /*#__PURE__*/_jsxs(EcosystemReviewWizard, {
    defaultReview: defaultReview,
    HeaderComponent: WizardHeader,
    isEditing: isEditing,
    editReason: editReason,
    offeringId: offeringId,
    onChange: onChange,
    onClose: handleClose,
    onSuccess: handleSuccess,
    originalReview: originalReview,
    reviewFormatter: reviewFormatter,
    reviewId: editedReview && editedReview.get('id'),
    reviewType: "APP",
    validationSchema: validationSchema,
    children: [/*#__PURE__*/_jsxs(View, {
      marginBlockEnd: "400",
      as: "span",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h5",
        marginBlockEnd: "200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-reviews.wizard.app.header"
        })
      }), supportLink && /*#__PURE__*/_jsx(FormattedJSXMessage, {
        elements: {
          Link
        },
        message: "marketplace-ui-reviews.wizard.app.subheader_jsx",
        options: {
          appName,
          external: true,
          href: supportLink,
          onClick: () => {
            onTrackInteraction({
              action: 'click support link',
              section: 'review'
            });
          }
        }
      })]
    }), /*#__PURE__*/_jsx(ReviewWizardRatingInput, {
      fieldKey: "OVERALL",
      label: unescapedText('marketplace-ui-reviews.wizard.app.labels.OVERALL')
    }), !!overallRating && /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(Button, {
        onClick: () => {
          onTrackInteraction({
            action: showDetailedRatings ? 'hide detailed ratings' : 'show detailed ratings',
            section: 'review'
          });
          setShowDetailedRatings(!showDetailedRatings);
        },
        use: "link",
        children: !showDetailedRatings ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: `marketplace-ui-reviews.wizard.app.detailedRatings.${isEditing ? 'edit' : 'write'}.show`
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: `marketplace-ui-reviews.wizard.app.detailedRatings.${isEditing ? 'edit' : 'write'}.hide`
        })
      }), showDetailedRatings && /*#__PURE__*/_jsx(Tile, {
        paddingBlockEnd: "400",
        paddingInlineStart: "400",
        children: DETAILED_RATING_AXES.map(axis => /*#__PURE__*/_jsx(ReviewWizardRatingInput, {
          fieldKey: axis,
          label: unescapedText(`marketplace-ui-reviews.wizard.app.labels.${axis}`),
          tooltip: unescapedText(`marketplace-ui-reviews.wizard.app.tooltips.${axis}`),
          type: "pill"
        }, axis))
      }), isEditing && /*#__PURE__*/_jsx(FormControl, {
        label: unescapedText('marketplace-ui-reviews.wizard.app.labels.editReason'),
        required: true,
        children: /*#__PURE__*/_jsx(Select, {
          onChange: ({
            target: {
              value
            }
          }) => {
            setEditReason(value);
          },
          options: EDIT_REASONS.map(reason => ({
            value: reason,
            text: unescapedText(`marketplace-ui-reviews.wizard.app.editReasons.${reason}`)
          }))
        })
      }), ['REVIEW', 'PROS', 'CONS'].map(axis => /*#__PURE__*/_jsx(ReviewWizardTextInput, {
        fieldKey: axis,
        label: unescapedText(`marketplace-ui-reviews.wizard.app.labels.${axis}`),
        placeholder: unescapedText(`marketplace-ui-reviews.wizard.app.placeholders.${axis}`, {
          appName
        })
      }, axis)), /*#__PURE__*/_jsx(ReviewWizardTextInput, {
        fieldKey: "TITLE",
        injectedinput: injectedTitle,
        label: unescapedText('marketplace-ui-reviews.wizard.app.labels.TITLE'),
        onValueChange: () => {
          setCustomTitleProvided(true);
          setInjectedTitle(undefined);
        },
        placeholder: generativeAiEnabled ? loading ? unescapedText('marketplace-ui-reviews.wizard.app.placeholders.TITLE_LOADING') : unescapedText('marketplace-ui-reviews.wizard.app.placeholders.TITLE_DEFAULT') : unescapedText('marketplace-ui-reviews.wizard.app.placeholders.TITLE'),
        resize: false,
        rows: 1
      }), /*#__PURE__*/_jsxs(View, {
        marginBlockStart: "50",
        children: [loading && customTitleProvided && /*#__PURE__*/_jsx(Text, {
          as: "span",
          typography: "detail-100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-ui-reviews.wizard.app.placeholders.TITLE_LOADING"
          })
        }), !loading && generatedTitle && customTitleProvided && /*#__PURE__*/_jsxs("span", {
          children: [/*#__PURE__*/_jsx(Text, {
            as: "span",
            typography: "heading-50",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-ui-reviews.wizard.app.suggestedTitle"
            })
          }), /*#__PURE__*/_jsx(Text, {
            as: "span",
            marginInline: "100",
            children: generatedTitle
          }), /*#__PURE__*/_jsx(Button, {
            marginBlockStart: "-50",
            onClick: () => {
              setCustomTitleProvided(false);
              setInjectedTitle(generatedTitle);
            },
            padding: "0",
            use: "transparent",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-ui-reviews.wizard.app.useSuggestedTitle"
            })
          })]
        })]
      }), /*#__PURE__*/_jsx(HR, {
        marginBlockStart: "800"
      }), /*#__PURE__*/_jsx(ReviewWizardTextInput, {
        fieldKey: "FEEDBACK",
        label: unescapedText('marketplace-ui-reviews.wizard.app.labels.FEEDBACK'),
        placeholder: unescapedText('marketplace-ui-reviews.wizard.app.placeholders.FEEDBACK', {
          appName
        })
      })]
    })]
  });
}